import i18next from 'i18next';
import * as ImagePicker from 'expo-image-picker';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';

import PermissionService from './PermissionService';

export type ImagePickerAssetType = ImagePicker.ImagePickerAsset;
export type ImagePickerSuccessResultType =
  ImagePicker.ImagePickerSuccessResult & { granted: true };
export type ImagePickerCanceledResultType =
  ImagePicker.ImagePickerCanceledResult & { granted: true };
export type ImagePickerNotGrantedResultType = {
  granted: false;
  canceled: false;
  assets: null;
};

export type ImagePickerResultType =
  | ImagePickerSuccessResultType
  | ImagePickerCanceledResultType
  | ImagePickerNotGrantedResultType;

type MediaLibraryPermissionOptionsType = {
  writeOnly?: boolean;
};

const PERMISSION_NOT_GRANTED_RESULT: ImagePickerNotGrantedResultType =
  {
    granted: false,
    canceled: false,
    assets: null,
  };

export interface IImagePickerOpenOptions
  extends ImagePicker.ImagePickerOptions,
    Omit<IResizeImageParams, 'source'> {}

interface IResizeImageParams {
  source: ImagePickerAssetType;
  width?: number;
  height?: number;
  format?: SaveFormat;
}

class ImagePickerService {
  requestPickerPermissionsAsync() {
    return PermissionService.request(
      {
        getPermission: (
          options?: MediaLibraryPermissionOptionsType,
        ) =>
          ImagePicker.getMediaLibraryPermissionsAsync(
            options?.writeOnly,
          ),
        requestPermission: (
          options?: MediaLibraryPermissionOptionsType,
        ) =>
          ImagePicker.requestMediaLibraryPermissionsAsync(
            options?.writeOnly,
          ),
      },
      {
        denied: {
          directToSettings: true,
          title: i18next.t(
            'services.imagePicker.photoLibraryPermissionTitle',
          ),
          message: i18next.t(
            'services.imagePicker.photoLibraryPermissionSubtitle',
          ),
        },
      },
    );
  }

  requestCameraPermissionsAsync() {
    return PermissionService.request(
      {
        getPermission: ImagePicker.getCameraPermissionsAsync,
        requestPermission: ImagePicker.requestCameraPermissionsAsync,
      },
      {
        denied: {
          directToSettings: true,
          title: i18next.t(
            'services.imagePicker.cameraPermissionTitle',
          ),
          message: i18next.t(
            'services.imagePicker.cameraPermissionSubtitle',
          ),
        },
      },
    );
  }

  private async _processResult(
    assets: ImagePickerAssetType[],
    options?: IImagePickerOpenOptions,
  ): Promise<ImagePickerAssetType[]> {
    return Promise.all(
      assets.map((asset) => {
        let resultType = asset.type;

        if (!resultType) {
          resultType = asset.uri.match(/data:video\/*/gim)
            ? 'video'
            : 'image';
        }

        if (
          resultType === 'image' &&
          (options?.width || options?.height)
        ) {
          const resized = this._resizeImage({
            source: {
              ...asset,
              type: resultType,
            },
            width: options.width,
            height: options.height,
            format: options?.format,
          });

          return {
            ...resized,
            ...asset,
          };
        }

        return asset;
      }),
    );
  }

  async openPicker(
    options?: IImagePickerOpenOptions,
  ): Promise<ImagePickerResultType> {
    const permissions = await this.requestPickerPermissionsAsync();

    if (!permissions.granted) {
      return PERMISSION_NOT_GRANTED_RESULT;
    }

    const result = await ImagePicker.launchImageLibraryAsync(options);

    if (result.canceled) {
      return {
        canceled: true,
        granted: true,
        assets: null,
      };
    }

    const processedResult = await this._processResult(
      result.assets,
      options,
    );

    return {
      canceled: false,
      granted: true,
      assets: processedResult,
    };
  }

  async openCamera(
    options?: IImagePickerOpenOptions,
  ): Promise<ImagePickerResultType> {
    const permissions = await this.requestCameraPermissionsAsync();

    if (!permissions.granted) {
      return PERMISSION_NOT_GRANTED_RESULT;
    }

    const result = await ImagePicker.launchCameraAsync(options);

    if (result.canceled) {
      return {
        canceled: true,
        granted: true,
        assets: null,
      };
    }

    const processedResult = await this._processResult(
      result.assets,
      options,
    );

    return {
      canceled: false,
      granted: true,
      assets: processedResult,
    };
  }

  // Sending original photos to the server is inefficient because the file size may too large (server burst).
  // Therefore, the resizing process is necessary
  async _resizeImage({
    source,
    width,
    height,
    format = SaveFormat.JPEG,
  }: IResizeImageParams): Promise<ImagePickerAssetType> {
    // Offsetting the X,Y axis to crop the center of the image
    let crop;
    if (source.height >= source.width) {
      crop = {
        originX: 0,
        originY: source.height / 2 - source.width / 2,
        width: source.width,
        height: source.width,
      };
    } else {
      crop = {
        originX: source.width / 2 - source.height / 2,
        originY: 0,
        width: source.height,
        height: source.height,
      };
    }

    // Resizing image to provided size
    const resize = {
      width,
      height,
    };

    const image = await ImageManipulator.manipulate(source.uri)
      .crop(crop)
      .resize(resize)
      .renderAsync();

    const result = await image.saveAsync({ format });

    return {
      ...source,
      ...result,
    };
  }
}

export default new ImagePickerService();
